/* =========================================================
   Shared list behaviour: column sorting across the whole
   result set (not just the visible page), pagination, and a
   records-per-page selector. Every table in the app uses this.
   ========================================================= */
import { useCallback, useState } from 'react';
import type { SortState } from '@/types';

export interface SortAccessor<T> {
  type: 'text' | 'number';
  get: (row: T) => unknown;
}

export type SortMap<T> = Record<string, SortAccessor<T>>;

export interface TableState {
  page: number;
  setPage: (p: number) => void;
  per: number;
  onPer: (n: number) => void;
  sort: SortState | null;
  onSort: (key: string) => void;
}

export function useTableState(defaultPer = 12): TableState {
  const [page, setPage] = useState(1);
  const [per, setPer] = useState(defaultPer);
  const [sort, setSort] = useState<SortState | null>(null);

  const onSort = useCallback((key: string) => {
    setSort((s) => (s && s.key === key ? { key, dir: s.dir === 'asc' ? 'desc' : 'asc' } : { key, dir: 'asc' }));
    setPage(1);
  }, []);

  const onPer = useCallback((n: number) => {
    setPer(n);
    setPage(1);
  }, []);

  return { page, setPage, per, onPer, sort, onSort };
}

/** Sorts the full set. Text uses a Spanish locale compare so accents and
 *  embedded numbers order the way a Spanish-speaking reader expects. */
export function sortRows<T>(rows: T[], sort: SortState | null, values: SortMap<T>): T[] {
  if (!sort?.key || !values[sort.key]) return rows;
  const { type, get } = values[sort.key];
  const dir = sort.dir === 'asc' ? 1 : -1;
  return [...rows].sort((a, b) => {
    const va = get(a);
    const vb = get(b);
    const c =
      type === 'number'
        ? (Number(va) || 0) - (Number(vb) || 0)
        : String(va ?? '').localeCompare(String(vb ?? ''), 'es', { numeric: true, sensitivity: 'base' });
    return c * dir;
  });
}

export interface Paged<T> {
  rows: T[];
  page: number;
  pageCount: number;
}

export function paginate<T>(rows: T[], page: number, per: number): Paged<T> {
  const pageCount = Math.max(1, Math.ceil(rows.length / per));
  const p = Math.min(page, pageCount);
  return { rows: rows.slice((p - 1) * per, p * per), page: p, pageCount };
}
